import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  signal,
  viewChild,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuService, MenuNode } from '../../services/menu';
import { PartnerKycService } from '../../services/partner-kyc';
import { AuthService } from '../../services/auth';
import { ToastService } from '../../services/toast';
import { StorageService } from '../../services/storage';
import { SearchService } from '../../services/search';

/** One row in the spotlight list — a flattened menu entry, quick action, or data hit. */
interface SearchItem {
  title: string;
  route: string;
  icon: string;
  /** Breadcrumb of parent menu titles, e.g. "Payments › Masters". */
  path: string;
  group: string;
  /** Second line for data hits, e.g. "AK-000123 · 9876543210 · PAID". */
  sub?: string;
  /** Query params for deep links (e.g. { q: memberUniqueId }); data hits only. */
  qp?: Record<string, string>;
  /** Router commands for a path-segment deep link (e.g. ['/payments/detail', 5]); data hits only. */
  nav?: any[];
}

const RECENT_KEY = 'gs_recent';
const MAX_RECENT = 5;

/**
 * Global search — a Ctrl+K / ⌘K spotlight over everything the user's MENU grants
 * (so access control stays menu-driven), plus quick account actions. Renders its
 * own topbar trigger; drop <app-global-search /> into the shell's topbar.
 */
@Component({
  selector: 'app-global-search',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './global-search.html',
  styleUrls: ['./global-search.css'],
})
export class GlobalSearch implements OnInit, OnDestroy {
  private menu = inject(MenuService);
  private router = inject(Router);
  private kyc = inject(PartnerKycService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private storage = inject(StorageService);
  private search = inject(SearchService);
  private platformId = inject(PLATFORM_ID);

  private box = viewChild<ElementRef<HTMLInputElement>>('box');

  open = signal(false);
  query = signal('');
  active = signal(0);
  recent = signal<SearchItem[]>([]);

  /** Live data hits (members / payments) for the current query. */
  dataRows = signal<SearchItem[]>([]);
  dataLoading = signal(false);
  private dataTimer: any = null;
  /** Monotonic guard — a response only lands if it belongs to the latest query. */
  private searchSeq = 0;

  /** Routes a KYC-locked partner may still open (mirrors the shell/guard allow-list). */
  private static readonly KYC_OPEN = new Set(['/partner-kyc', '/forbidden', '/change-password']);

  private readonly QUICK: SearchItem[] = [
    { title: 'Change Password', route: '/change-password', icon: 'pi pi-key', path: 'Account', group: 'Quick actions' },
  ];

  /** The user's menu, flattened to routable entries with a breadcrumb path. */
  private readonly items = computed<SearchItem[]>(() => {
    const out: SearchItem[] = [];
    const walk = (nodes: MenuNode[], trail: string[]) => {
      for (const n of nodes ?? []) {
        const route = (n.route || '').trim();
        if (route) {
          out.push({
            title: n.title,
            route: route.startsWith('/') ? route : '/' + route,
            icon: this.iconOf(n.icon),
            path: trail.join(' › '),
            group: 'Pages',
          });
        }
        if (n.children?.length) walk(n.children, [...trail, n.title]);
      }
    };
    walk(this.menu.myMenu() as MenuNode[], []);
    return out;
  });

  /** Flat list in render order — sections are derived from each row's `group`. */
  readonly rows = computed<SearchItem[]>(() => {
    const q = this.query().trim().toLowerCase();
    if (q) {
      const hit = (s: string) => s.toLowerCase().includes(q);
      return [
        ...[
          ...this.items().filter((i) => hit(i.title) || hit(i.path)),
          ...this.QUICK.filter((i) => hit(i.title)),
        ].slice(0, 8),
        // live member / payment hits (already grouped + capped by dataSearch)
        ...this.dataRows(),
      ];
    }
    const seen = new Set<string>();
    const take = (list: SearchItem[], group: string, max: number) =>
      list
        .filter((i) => !seen.has(i.route) && (seen.add(i.route), true))
        .slice(0, max)
        .map((i) => ({ ...i, group }));
    return [
      ...take(this.recent(), 'Recent', MAX_RECENT),
      ...take(this.items(), 'Suggestions', 6),
      ...take(this.QUICK, 'Quick actions', this.QUICK.length),
    ];
  });

  // Global hotkey — a named handler so ngOnDestroy can remove exactly what we added.
  private onGlobalKey = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
      e.preventDefault();
      this.open() ? this.close() : this.openSearch();
    } else if (e.key === 'Escape' && this.open()) {
      this.close();
    }
  };

  constructor() {
    // Focus the input as soon as the panel renders.
    effect(() => {
      if (this.open() && isPlatformBrowser(this.platformId)) {
        queueMicrotask(() => this.box()?.nativeElement?.focus());
      }
    });
  }

  ngOnInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    document.addEventListener('keydown', this.onGlobalKey);
    this.recent.set(this.loadRecent());
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('keydown', this.onGlobalKey);
    }
  }

  openSearch() {
    this.query.set('');
    this.active.set(0);
    this.dataRows.set([]);
    this.open.set(true);
  }

  close() {
    this.open.set(false);
    if (this.dataTimer) clearTimeout(this.dataTimer);
    this.searchSeq++; // orphan any in-flight lookups
    this.dataLoading.set(false);
  }

  onQuery(v: string) {
    this.query.set(v);
    this.active.set(0);
    // Debounced record search (members + payments) once the term is meaningful.
    if (this.dataTimer) clearTimeout(this.dataTimer);
    const term = v.trim();
    if (term.length < 3 || !this.canSearchData()) {
      this.searchSeq++;
      this.dataRows.set([]);
      this.dataLoading.set(false);
      return;
    }
    this.dataTimer = setTimeout(() => this.dataSearch(term), 300);
  }

  /** KYC-locked partners can't open the target pages — don't tease them with hits. */
  private canSearchData(): boolean {
    if (this.auth.isAdmin()) return true;
    return !this.kyc.loaded() || this.kyc.isApproved();
  }

  /**
   * Live record search via the keyless universal endpoint (GET /api/Search/All). One term
   * is matched across every member + payment identifier; the backend scopes to the caller's
   * records. Clicking a MEMBER opens its lookup view; clicking a PAYMENT opens that payment's
   * receipt page. Max 6 hits per section.
   */
  private dataSearch(term: string) {
    const seq = ++this.searchSeq;
    this.dataLoading.set(true);

    this.search.universal(term, 6).subscribe({
      next: (res: any) => {
        if (seq !== this.searchSeq) return;
        const members: SearchItem[] = [];
        const payments: SearchItem[] = [];
        if (res?.success && res.data) {
          for (const m of (res.data.members ?? []).slice(0, 6)) {
            members.push({
              title: m.fullName || m.memberUniqueId || `Member #${m.id}`,
              sub: [m.memberUniqueId, m.mobileNumber, m.paymentStatus].filter(Boolean).join(' · '),
              route: '/member-lookup',
              qp: { q: m.memberUniqueId || String(m.id) },
              icon: 'pi pi-user',
              path: '',
              group: 'Members',
            });
          }
          for (const p of (res.data.payments ?? []).slice(0, 6)) {
            payments.push({
              title: p.paymentUniqueId || `Payment #${p.id}`,
              sub: [p.memberName, p.amount != null ? `₹${p.amount}` : '', p.paymentStatus]
                .filter(Boolean)
                .join(' · '),
              // Open THAT payment's details page (works for any status; scoped server-side).
              route: '/payments/detail',
              nav: ['/payments/detail', p.id],
              icon: 'pi pi-indian-rupee',
              path: '',
              group: 'Payments',
            });
          }
        }
        this.dataRows.set([...members, ...payments]);
        this.dataLoading.set(false);
      },
      error: () => {
        if (seq !== this.searchSeq) return;
        this.dataRows.set([]);
        this.dataLoading.set(false);
      },
    });
  }

  /** Keyboard driving inside the input: arrows move, Enter opens, Esc closes. */
  onKey(e: KeyboardEvent) {
    const n = this.rows().length;
    if (e.key === 'ArrowDown' && n) {
      e.preventDefault();
      this.active.set((this.active() + 1) % n);
    } else if (e.key === 'ArrowUp' && n) {
      e.preventDefault();
      this.active.set((this.active() - 1 + n) % n);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = this.rows()[this.active()];
      if (item) this.go(item);
    } else if (e.key === 'Escape') {
      this.close();
    }
  }

  go(item: SearchItem) {
    this.close();
    if (this.isLocked(item)) {
      const { title, text } = this.kyc.blockedAlert();
      this.toast.popup(text, title, 'warning');
      return;
    }
    if (item.nav) {
      // Data hit with a path-segment target (e.g. /payments/detail/5); transient, not recorded.
      this.router.navigate(item.nav);
      return;
    }
    if (item.qp) {
      // Data hit — deep-link with query params; transient, so not stored in recents.
      this.router.navigate([item.route], { queryParams: item.qp });
      return;
    }
    this.pushRecent(item);
    this.router.navigate([item.route]);
  }

  /** A page a KYC-locked partner can't open yet (admins are never locked). */
  isLocked(item: SearchItem): boolean {
    if (this.auth.isAdmin()) return false;
    if (!this.kyc.loaded() || this.kyc.isApproved()) return false;
    return !GlobalSearch.KYC_OPEN.has(item.route);
  }

  /** Split a title around the query so the template can highlight the match. */
  parts(title: string): { pre: string; hit: string; post: string } {
    const q = this.query().trim();
    if (!q) return { pre: title, hit: '', post: '' };
    const at = title.toLowerCase().indexOf(q.toLowerCase());
    if (at < 0) return { pre: title, hit: '', post: '' };
    return { pre: title.slice(0, at), hit: title.slice(at, at + q.length), post: title.slice(at + q.length) };
  }

  // ===================== recents (localStorage, SSR-safe via StorageService) =====================
  private loadRecent(): SearchItem[] {
    try {
      const raw = this.storage.getLocal(RECENT_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.slice(0, MAX_RECENT) : [];
    } catch {
      return [];
    }
  }

  private pushRecent(item: SearchItem) {
    const next = [
      { ...item, group: 'Recent' },
      ...this.recent().filter((r) => r.route !== item.route),
    ].slice(0, MAX_RECENT);
    this.recent.set(next);
    this.storage.setLocal(RECENT_KEY, JSON.stringify(next));
  }

  private iconOf(icon?: string | null): string {
    const v = (icon || '').trim();
    if (!v) return 'pi pi-circle';
    if (v.startsWith('pi ')) return v;
    if (v.startsWith('pi-')) return `pi ${v}`;
    return `pi pi-${v}`;
  }
}
